'use client';

import { Search } from 'lucide-react';
import * as React from 'react';

import { useControl } from 'src/hooks';
import { cn } from 'src/lib/utils';

import { Button } from '../ui/button';

import { Input, InputProps } from './input';

export interface SearchInputProps
  extends Omit<InputProps, 'suffix' | 'onEnter' | 'defaultValue' | 'value' | 'clearable'> {
  // 搜索回调
  onSearch?: (value: string) => void;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ onSearch, onValueChange, className, defaultValue, value, ...props }, ref) => {
    const [currentValue, setCurrentValue] = useControl(defaultValue ?? '', value, onValueChange);
    const [clearable, setClearable] = React.useState(() => {
      // 如果父组件传入了值，说明处于已搜索状态
      const initialValue = value ?? defaultValue ?? '';
      return !!initialValue;
    });

    // 处理搜索
    const handleSearch = () => {
      onSearch?.(currentValue);
      if (currentValue) setClearable(true);
    };

    const handleInputChange = (val: string) => {
      setCurrentValue(val);
      setClearable(false);
    };

    // 搜索图标按钮
    const searchButton = (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleSearch}
        className="h-6 w-6 -mr-1 text-muted-foreground hover:text-accent-foreground"
        tabIndex={-1}
        aria-label="Search"
      >
        <Search className="h-4 w-4" />
      </Button>
    );

    return (
      <Input
        {...props}
        ref={ref}
        value={currentValue}
        suffix={clearable ? undefined : searchButton}
        onEnter={handleSearch}
        onValueChange={handleInputChange}
        className={cn('pr-9', className)} // Update this line
        clearable={clearable}
      />
    );
  }
);

SearchInput.displayName = 'SearchInput';

export { SearchInput };
